import React, { useState, useEffect } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ja } from 'date-fns/locale';
import { Button } from '@/components/Button';
import { MaterialIcon } from '@/components/MaterialIcon';
import Select from '@/components/Select';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import useFetchExportableAttributes from '@/features/agent/export_requests/api/useFetchExportableAttributes';
import usePostExportRequests from '@/features/agent/export_requests/api/usePostExportRequests';
import {
  DATE_COLUMN_OPTIONS,
  JOB_CHANGE_PROCESS_LOG_STEP_OPTIONS,
  type CreateExportRequestParams,
  type ResourceType,
  type EncodingType,
} from '@/features/agent/export_requests/types/ExportRequest';
import { showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import { showLoadingToast } from '@/features/agent/job_change_processes/lib/toast';

registerLocale('ja', ja);

interface ExportItemSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (exportId: number, tempId: number) => void;
  selectedDataType: string;
}

const ExportItemSelectionModal: React.FC<ExportItemSelectionModalProps> = ({
  isOpen,
  onClose,
  onExport,
  selectedDataType,
}) => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [selectedCustomFieldIds, setSelectedCustomFieldIds] = useState<number[]>([]);
  const [encoding, setEncoding] = useState<EncodingType>('utf8');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;
  const [dateColumn, setDateColumn] = useState<string>('created_at');
  const [selectedSteps, setSelectedSteps] = useState<string[]>([]);

  const dateColumnOptions =
    DATE_COLUMN_OPTIONS[selectedDataType as ResourceType] || DATE_COLUMN_OPTIONS.job_post;

  // エクスポート可能な属性を取得
  const { data: exportableAttributes } = useFetchExportableAttributes(selectedDataType, {
    enabled: isOpen && !!selectedDataType && selectedDataType !== '',
  });
  const { mutateAsync: postExportRequest, isPending } = usePostExportRequests();

  // 初期状態で全ての項目を選択済みにする
  useEffect(() => {
    if (exportableAttributes) {
      const allColumns = exportableAttributes.columns?.map((col) => col.column) || [];
      const allCustomFieldIds = exportableAttributes.custom_fields?.map((field) => field.id) || [];
      setSelectedColumns(allColumns);
      setSelectedCustomFieldIds(allCustomFieldIds);
    }
  }, [exportableAttributes]);

  // モーダルが閉じたときに選択状態をリセット
  useEffect(() => {
    if (!isOpen) {
      setSelectedColumns([]);
      setSelectedCustomFieldIds([]);
      setEncoding('utf8');
      setDateRange([null, null]);
      setDateColumn('created_at');
      setSelectedSteps([]);
    }
  }, [isOpen]);

  const encodingOptions = [
    { value: 'utf8' as const, label: 'UTF-8（BOMなし）' },
    { value: 'utf8_bom' as const, label: 'UTF-8（BOMつき）' },
    { value: 'shift_jis' as const, label: 'Shift_JIS（Windows推奨）' },
  ];

  const handleColumnToggle = (column: string) => {
    setSelectedColumns((prev) =>
      prev.includes(column) ? prev.filter((col) => col !== column) : [...prev, column]
    );
  };

  const handleCustomFieldToggle = (fieldId: number) => {
    setSelectedCustomFieldIds((prev) =>
      prev.includes(fieldId) ? prev.filter((id) => id !== fieldId) : [...prev, fieldId]
    );
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
    setSelectedCustomFieldIds([]);
  };

  const getDataTypeLabel = (dataType: string): string => {
    switch (dataType) {
      case 'job_post':
        return '求人データ';
      case 'candidate_profile':
        return '求職者データ';
      case 'job_change_process':
        return 'マッチングデータ';
      case 'company':
        return '企業データ';
      case 'candidate_interview_log':
        return '面談情報データ';
      case 'job_post_endpoint':
        return '求人推薦データ';
      case 'job_change_process_log':
        return '選考ログデータ';
      default:
        return 'データ';
    }
  };

  const handleStepToggle = (step: string) => {
    setSelectedSteps((prev) =>
      prev.includes(step) ? prev.filter((item) => item !== step) : [...prev, step]
    );
  };

  const formatDateToString = (date: Date | null): string | null => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleExportSubmit = async () => {
    // 一時的なIDを生成
    const tempId = Date.now();

    try {
      const params: CreateExportRequestParams = {
        resource_type: selectedDataType as ResourceType,
        column: selectedColumns,
        custom_field_ids: selectedCustomFieldIds,
        encoding: encoding,
        date_from: formatDateToString(startDate),
        date_to: formatDateToString(endDate),
        date_column: startDate || endDate ? dateColumn : undefined,
        steps: selectedDataType === 'job_change_process_log' ? selectedSteps : [],
      };

      // POSTリクエストを実行
      const response = await postExportRequest(params);

      // 実際のエクスポートIDを取得
      const actualExportId = response?.id || response?.export_request?.id;
      const exportStatus = response?.status || response?.export_request?.status;

      if (actualExportId) {
        // statusがrunningの場合のみトーストを表示
        if (exportStatus === 'running') {
          const dataTypeLabel = getDataTypeLabel(selectedDataType);
          showLoadingToast(`${dataTypeLabel}をエクスポートしています。`);
        }

        onExport(actualExportId, tempId); // 実際のIDとトーストIDを渡す
      }

      onClose(); // モーダルを閉じる
    } catch (error) {
      showErrorToast('エクスポートに失敗しました。');
    }
  };

  const totalSelectedItems = selectedColumns.length + selectedCustomFieldIds.length;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent className="flex max-h-[90vh] w-[860px] max-w-[95vw] flex-col rounded-lg shadow-xl!">
        <div className="flex shrink-0 items-center justify-between border-b border-solid border-gray-200 px-6 py-[25px]">
          <DialogTitle className="text-base! font-bold! text-[#262626]">
            {getDataTypeLabel(selectedDataType)}項目の選択
          </DialogTitle>
          <button
            onClick={handleDeselectAll}
            disabled={totalSelectedItems === 0}
            className={`flex items-center gap-[5px] text-sm text-primary hover:text-blue-800 ${
              totalSelectedItems === 0 ? 'cursor-not-allowed opacity-50' : ''
            }`}
          >
            <MaterialIcon name="close" className="text-xl text-primary" />
            全ての選択を解除
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {/* エクスポート項目選択 */}
          <div className="mb-6">
            <h3 className="mb-4 text-sm font-bold text-[#262626]">
              エクスポートする項目を選択してください
            </h3>

            <div className="grid grid-cols-3 gap-4">
              {/* 基本項目 */}
              {exportableAttributes?.columns?.map((col) => (
                <label key={`column-${col.column}`} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(col.column)}
                    onChange={() => handleColumnToggle(col.column)}
                    className="size-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{col.label}</span>
                </label>
              ))}

              {/* カスタムフィールド */}
              {exportableAttributes?.custom_fields?.map((field) => (
                <label key={`custom-${field.id}`} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedCustomFieldIds.includes(field.id)}
                    onChange={() => handleCustomFieldToggle(field.id)}
                    className="size-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{field.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 期間指定 */}
          <div className="mb-6">
            <h3 className="mb-2 text-sm font-bold text-[#262626]">期間指定（任意）</h3>
            <p className="mb-3 text-xs text-gray-500">
              指定しない場合は全期間のデータが出力されます
            </p>
            <div className="flex items-center gap-3">
              <div className="w-[160px]">
                <Select
                  value={dateColumn}
                  onValueChange={setDateColumn}
                  items={dateColumnOptions}
                  defaultLabel="対象日付"
                />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <div className="relative flex h-[38px] w-[180px] items-center rounded border border-solid border-gray-300 focus-within:border-blue-500">
                <DatePicker
                  locale="ja"
                  selected={startDate}
                  onChange={(date: Date | null) => setDateRange([date, endDate])}
                  selectsStart
                  startDate={startDate as Date | undefined}
                  endDate={endDate as Date | undefined}
                  maxDate={endDate || undefined}
                  dateFormat="yyyy/MM/dd"
                  placeholderText="開始日"
                  className="size-full rounded border-none px-3 text-sm focus:outline-hidden"
                />
                {startDate && (
                  <button
                    type="button"
                    onClick={() => setDateRange([null, endDate])}
                    className="absolute right-2 flex items-center justify-center text-gray-400 hover:text-gray-600"
                  >
                    <MaterialIcon name="close" className="text-base" />
                  </button>
                )}
              </div>
              <span className="text-sm text-gray-500">〜</span>
              <div className="relative flex h-[38px] w-[180px] items-center rounded border border-solid border-gray-300 focus-within:border-blue-500">
                <DatePicker
                  locale="ja"
                  selected={endDate}
                  onChange={(date: Date | null) => setDateRange([startDate, date])}
                  selectsEnd
                  startDate={startDate as Date | undefined}
                  endDate={endDate as Date | undefined}
                  minDate={startDate || undefined}
                  dateFormat="yyyy/MM/dd"
                  placeholderText="終了日"
                  className="size-full rounded border-none px-3 text-sm focus:outline-hidden"
                />
                {endDate && (
                  <button
                    type="button"
                    onClick={() => setDateRange([startDate, null])}
                    className="absolute right-2 flex items-center justify-center text-gray-400 hover:text-gray-600"
                  >
                    <MaterialIcon name="close" className="text-base" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {selectedDataType === 'job_change_process_log' && (
            <div className="mb-6">
              <h3 className="mb-2 text-sm font-bold text-[#262626]">ステップ種別（任意）</h3>
              <p className="mb-3 text-xs text-gray-500">指定しない場合は全ステップを出力します</p>
              <div className="grid grid-cols-3 gap-3">
                {JOB_CHANGE_PROCESS_LOG_STEP_OPTIONS.map((step) => (
                  <label key={step.value} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedSteps.includes(step.value)}
                      onChange={() => handleStepToggle(step.value)}
                      className="size-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{step.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* 文字コード設定 */}
          <div className="">
            <h3 className="mb-2 text-sm font-bold text-[#262626]">文字コード設定</h3>
            <div className="w-48">
              <Select
                value={encoding}
                onValueChange={(value: string) => setEncoding(value as EncodingType)}
                items={encodingOptions}
                defaultLabel="文字コードを選択"
              />
            </div>
          </div>
        </div>

        {/* ボタン */}
        <div className="flex shrink-0 justify-end gap-3 border-t border-solid border-gray-200 px-6 py-4">
          <Button color="secondary" size="md" onClick={onClose}>
            キャンセル
          </Button>
          <Button
            color="primary"
            size="md"
            onClick={handleExportSubmit}
            disabled={totalSelectedItems === 0 || isPending}
          >
            出力する
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportItemSelectionModal;
